import { apiUrl } from './api_config'

const API_BASE_URL = apiUrl('/royalties')
const GET_ROYALTIES_ENDPOINT = `${API_BASE_URL}/all`
const COMPUTE_ROYALTY_ENDPOINT = `${API_BASE_URL}/compute`
const ROYALTY_BOOK_OPTIONS_ENDPOINT = `${API_BASE_URL}/book-options`
const PAY_ROYALTY_ENDPOINT = `${API_BASE_URL}/payments`
const UPDATE_ROYALTY_PAYMENT_ENDPOINT = `${API_BASE_URL}/payments`
const DELETE_ROYALTY_PAYMENT_ENDPOINT = `${API_BASE_URL}/payments`

async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })

  if (!response.ok) {
    let message = `Royalty request failed (${response.status})`
    try {
      const error = await response.json()
      message = error.message || error.error || message
    } catch {
      // Keep the HTTP status message when the server does not return JSON.
    }
    throw new Error(message)
  }

  return response.status === 204 ? null : response.json()
}

function findList(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  for (const key of [
    'royalties',
    'royaltyPayments',
    'royalty_payments',
    'royaltyList',
    'results',
    'items',
    'data',
  ]) {
    const list = findList(data[key])
    if (list.length || Array.isArray(data[key])) return list
  }

  return []
}

function findNextCursor(data) {
  if (!data || typeof data !== 'object') return null
  return (
    data.nextCursor ??
    data.next_cursor ??
    data.nextPageToken ??
    data.next_page_token ??
    data.next ??
    findNextCursor(data.royalties) ??
    findNextCursor(data.royaltyPayments) ??
    findNextCursor(data.royalty_payments) ??
    findNextCursor(data.results) ??
    findNextCursor(data.data) ??
    findNextCursor(data.pagination) ??
    findNextCursor(data.meta) ??
    null
  )
}

export async function getRoyalties({
  cursor = null,
  page = 1,
  author = '',
  fromDate = '',
  toDate = '',
  signal,
} = {}) {
  const params = new URLSearchParams({ limit: '20' })
  if (cursor) params.set('cursor', cursor)
  else params.set('page', String(page))
  if (author) params.set('author', String(author))
  if (fromDate) params.set('from_date', fromDate)
  if (toDate) params.set('to_date', toDate)

  const data = await request(`${GET_ROYALTIES_ENDPOINT}?${params}`, { signal })
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data
  const nextCursor = findNextCursor(data)
  const currentPage = Number(
    payload?.page ?? payload?.currentPage ?? payload?.current_page ?? page,
  )
  const hasNext = Boolean(
    payload?.hasNext ??
    payload?.has_next ??
    data?.hasNext ??
    data?.has_next ??
    nextCursor,
  )

  return {
    royalties: findList(data),
    nextCursor,
    nextPage: hasNext && !nextCursor ? currentPage + 1 : null,
    currentPage,
  }
}

export async function computeRoyalty(payload) {
  const data = await request(COMPUTE_ROYALTY_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return data?.data ?? data
}

export async function payRoyalty(payload) {
  const data = await request(PAY_ROYALTY_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return data?.data ?? data
}

export async function getRoyaltyBookOptions() {
  const data = await request(ROYALTY_BOOK_OPTIONS_ENDPOINT)
  return Array.isArray(data?.data) ? data.data : []
}

export async function getRoyaltyPayments({
  cursor = null,
  page = 1,
  author = '',
  fromDate = '',
  toDate = '',
  fromAmount = '',
  toAmount = '',
} = {}) {
  const params = new URLSearchParams({ limit: '20' })
  if (cursor) params.set('cursor', cursor)
  else params.set('page', String(page))
  if (author) params.set('author', String(author))
  if (fromDate) params.set('from_date', fromDate)
  if (toDate) params.set('to_date', toDate)
  if (fromAmount) params.set('from_amount', fromAmount)
  if (toAmount) params.set('to_amount', toAmount)

  const data = await request(`${PAY_ROYALTY_ENDPOINT}?${params}`)
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data
  const nextCursor = findNextCursor(data)
  const currentPage = Number(
    payload?.page ?? payload?.currentPage ?? payload?.current_page ?? page,
  )
  const hasNext = Boolean(
    payload?.hasNext ??
    payload?.has_next ??
    data?.hasNext ??
    data?.has_next ??
    nextCursor,
  )

  return {
    payments: findList(data),
    nextCursor,
    nextPage: hasNext && !nextCursor ? currentPage + 1 : null,
    currentPage,
  }
}

export async function updateRoyaltyPayment(id, payload) {
  const data = await request(
    `${UPDATE_ROYALTY_PAYMENT_ENDPOINT}/${encodeURIComponent(id)}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    },
  )
  return data?.data ?? data
}

export async function deleteRoyaltyPayment(id) {
  await request(
    `${DELETE_ROYALTY_PAYMENT_ENDPOINT}/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  )
}

export async function deleteRoyalty(id) {
  await request(`${API_BASE_URL}/deleteRoyalty/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}
